import React, { useState, useEffect, useRef } from "react";
import { Card } from "./cards/cardDisplay";
import { Modal } from "./modal/modalDisplay";
import NavBar from "../../Admin/Dashboard/NavBar";
import ReactProSidebar from "../../Admin/Dashboard/ReactProSidebar";
import "./../../../styles/App.css";
import "./ImageAnalysisNew.css";
import { Typography } from "@mui/material";
import { toast } from "react-toastify";
import { modelsApi } from "../../../API/axiosInstance";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolderOpen, faCloudArrowUp, faClipboard,
  faUpload, faXmark, faSpinner, faImage,
} from "@fortawesome/free-solid-svg-icons";


// ── Security constants ──
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
const BLOCKED_EXTENSIONS = [
  ".exe", ".php", ".jsp", ".asp", ".sh", ".bat", ".cmd",
  ".ps1", ".py", ".rb", ".pl", ".cgi", ".msi", ".dll",
  ".so", ".zip", ".tar", ".gz", ".rar", ".7z", ".jar",
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_DIMENSION = 4096;            // px — hard ceiling before canvas re-encode
const RATE_LIMIT_MAX = 5;              // uploads per window
const RATE_LIMIT_WINDOW_MS = 60_000;   // 1 minute

// ── Client-side rate limiter ──
const _uploadTimestamps = [];

function checkRateLimit() {
  const now = Date.now();
  const cutoff = now - RATE_LIMIT_WINDOW_MS;
  // Evict timestamps outside the window
  while (_uploadTimestamps.length && _uploadTimestamps[0] < cutoff) {
    _uploadTimestamps.shift();
  }
  if (_uploadTimestamps.length >= RATE_LIMIT_MAX) return false;
  _uploadTimestamps.push(now);
  return true;
}

// ── SSRF guard for URL input ──
function isPrivateUrl(urlString) {
  try {
    const { hostname, protocol } = new URL(urlString);
    if (!["http:", "https:"].includes(protocol)) return true;
    if (/^(localhost|.*\.local)$/i.test(hostname)) return true;
    if (/^127\./.test(hostname)) return true;
    if (/^0\.0\.0\.0/.test(hostname)) return true;
    if (/^10\./.test(hostname)) return true;
    if (/^172\.(1[6-9]|2\d|3[01])\./.test(hostname)) return true;
    if (/^192\.168\./.test(hostname)) return true;
    if (/^169\.254\./.test(hostname)) return true;
    if (/^\[?::1\]?$/.test(hostname)) return true;        // IPv6 loopback
    if (/^\[?fc[0-9a-f]{2}:/i.test(hostname)) return true; // IPv6 ULA
    return false;
  } catch {
    return true; // unparseable URL → block
  }
}

// ── Validate file name, extension, MIME ──
const isValidImage = (file) => {
  if (!ALLOWED_TYPES.includes(file.type)) return false;

  const fileName = file.name.toLowerCase();

  // Null-byte injection
  if (fileName.includes("\0")) return false;

  // Explicitly blocked dangerous extensions anywhere in the name
  if (BLOCKED_EXTENSIONS.some((ext) => fileName.includes(ext))) return false;

  // Must end with a known image extension
  const hasValidExt = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  if (!hasValidExt) return false;

  // Block double extensions like "shell.php.jpg"
  const nameParts = fileName.split(".");
  if (nameParts.length > 2) return false;

  // Block delimiter tricks like "image.jpg;.php"
  if (/[;/\\%]/.test(fileName)) return false;

  return true;
};

// ── Verify actual file content via magic bytes ──
const verifyImageMagicBytes = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      const isJpeg = arr[0] === 0xff && arr[1] === 0xd8 && arr[2] === 0xff;
      const isPng =
        arr[0] === 0x89 &&
        arr[1] === 0x50 &&
        arr[2] === 0x4e &&
        arr[3] === 0x47;
      const isGif =
        arr[0] === 0x47 &&
        arr[1] === 0x49 &&
        arr[2] === 0x46 &&
        arr[3] === 0x38;
      const isWebp =
        arr[0] === 0x52 &&
        arr[1] === 0x49 &&
        arr[2] === 0x46 &&
        arr[3] === 0x46 &&
        arr[8] === 0x57 &&
        arr[9] === 0x45 &&
        arr[10] === 0x42 &&
        arr[11] === 0x50;

      resolve(isJpeg || isPng || isGif || isWebp);
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
};

// ── Always re-encode through canvas ──
// Strips EXIF, ICC profiles, embedded scripts, and metadata regardless of
// file size. Also downsizes anything exceeding MAX_DIMENSION.
const sanitizeImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        let { naturalWidth: w, naturalHeight: h } = img;

        if (w === 0 || h === 0) return reject(new Error("Degenerate image dimensions"));

        if (w > MAX_DIMENSION || h > MAX_DIMENSION) {
          const scale = MAX_DIMENSION / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        // White background eliminates alpha-channel exploit vectors
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Re-encode failed"));
            resolve(new File([blob], file.name, { type: "image/jpeg" }));
          },
          "image/jpeg",
          0.88,
        );
      };
      img.onerror = () => reject(new Error("Invalid image content"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
};

// ── Main Dashboard Component ──
export default function ImageAnalysisMain() {
  const [images, setImages] = useState([]);
  const [urlInput, setUrlInput] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [clipboardImage, setClipboardImage] = useState(null);
  const ImageContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const fetchAllResults = async () => {
    try {
      const response = await modelsApi.get(`/results`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching results:", error);
    }
  };

  const handleFileUpload = async (file) => {
    if (loading) return;

    if (!file) {
      toast.error("No file selected.");
      return;
    }

    // Rate limit
    if (!checkRateLimit()) {
      toast.error("Too many uploads. Please wait a moment before trying again.");
      return;
    }

    // MIME + extension + filename safety
    if (!isValidImage(file)) {
      toast.error("Invalid file. Only JPEG, PNG, GIF, and WebP images are allowed.");
      return;
    }

    // Hard size gate before re-encoding
    if (file.size > MAX_FILE_SIZE * 4) {
      toast.error(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB before compression.`);
      return;
    }

    // Magic bytes — confirm actual content matches claimed type
    const isRealImage = await verifyImageMagicBytes(file);
    if (!isRealImage) {
      toast.error("File content does not match a recognised image format. Upload rejected.");
      return;
    }

    // Always sanitize: re-encode through canvas to strip EXIF, metadata,
    // embedded scripts, and ICC payloads. Also resizes if over MAX_DIMENSION.
    let sanitized;
    try {
      sanitized = await sanitizeImage(file);
    } catch {
      toast.error("Could not process the image. Please try a different file.");
      return;
    }

    if (sanitized.size > MAX_FILE_SIZE) {
      toast.error(`Processed image is still too large (${(sanitized.size / 1024 / 1024).toFixed(1)} MB). Please use a smaller image.`);
      return;
    }

    setLoading(true);

    // UUID filename — never expose original name to the server
    const safeFileName = `img_${crypto.randomUUID()}.jpg`;
    const safeFile = new File([sanitized], safeFileName, { type: "image/jpeg" });

    const formData = new FormData();
    formData.append("file", safeFile);

    try {
      const response = await modelsApi.post(`/image-analyze`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        await fetchAllResults();
      }
    } catch (error) {
      if (error.response?.status === 413) {
        toast.error("Image is too large for the server. Try a smaller image.");
      } else if (error.response?.status === 429) {
        toast.error("Upload limit reached on the server. Please wait before retrying.");
      } else {
        toast.error("Upload failed. Please try again.");
      }
      console.error("Upload error:", error);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUrlSubmit = async () => {
    const url = urlInput.trim();
    if (!url) {
      toast.error("Please enter a valid URL.");
      return;
    }

    // Block private/internal network addresses (SSRF prevention)
    if (isPrivateUrl(url)) {
      toast.error("URL points to a restricted address and cannot be fetched.");
      return;
    }

    setLoading(true);

    // Hard 10-second timeout to prevent slow-loris / resource exhaustion
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10_000);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      const blob = await response.blob();

      if (!ALLOWED_TYPES.includes(blob.type)) {
        toast.error("URL does not point to a valid image.");
        setLoading(false);
        return;
      }

      if (blob.size > MAX_FILE_SIZE * 4) {
        toast.error("Remote image is too large to download.");
        setLoading(false);
        return;
      }

      const ext = blob.type.split("/")[1] === "jpeg" ? "jpg" : blob.type.split("/")[1];
      const file = new File([blob], `url_image.${ext}`, { type: blob.type });
      await handleFileUpload(file);
      setUrlInput("");
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        toast.error("Request timed out. The URL took too long to respond.");
      } else {
        toast.error("Could not fetch image from URL. Try uploading the file directly.");
      }
      console.error("URL fetch error:", error);
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragging) {
      setIsDragging(true);
      setTimeout(() => {
        ImageContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 0);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handlePageDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handlePageDragLeave = (e) => {
    e.preventDefault();
    if (e.clientX === 0 && e.clientY === 0) {
      setIsDragging(false);
    }
  };

  const handlePageDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleUrlSubmit();
    }
  };

  const checkClipboard = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (
          item.types.includes("image/png") ||
          item.types.includes("image/jpeg")
        ) {
          const imageType = item.types.find((type) =>
            type.startsWith("image/"),
          );
          const blob = await item.getType(imageType);
          setClipboardImage(blob);
          return;
        }
      }
      setClipboardImage(null);
    } catch (error) {
      setClipboardImage(null);
    }
  };

  const handleClipboardUpload = () => {
    if (clipboardImage) {
      const file = new File([clipboardImage], "clipboard-image.png", {
        type: clipboardImage.type,
      });
      handleFileUpload(file);
      setClipboardImage(null);
    }
  };

  const handleBrowseClick = () => {
    toast.info("You Can Also Drag and Drop Images Here!", {
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    fileInputRef.current.click();
  };

  const openModal = (id) => {
    setSelectedId(id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handlePrev = () => {
    const currentIndex = images.findIndex((img) => img._id === selectedId);
    if (currentIndex > 0) {
      setSelectedId(images[currentIndex - 1]._id);
    }
  };

  const handleNext = () => {
    const currentIndex = images.findIndex((img) => img._id === selectedId);
    if (currentIndex < images.length - 1) {
      setSelectedId(images[currentIndex + 1]._id);
    }
  };

  useEffect(() => {
    fetchAllResults();
    pollIntervalRef.current = setInterval(() => {
      fetchAllResults();
    }, 120000);

    checkClipboard();
    window.addEventListener("focus", checkClipboard);
    window.addEventListener("dragover", handlePageDragOver);
    window.addEventListener("dragleave", handlePageDragLeave);
    window.addEventListener("drop", handlePageDrop);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      window.removeEventListener("focus", checkClipboard);
      window.removeEventListener("dragover", handlePageDragOver);
      window.removeEventListener("dragleave", handlePageDragLeave);
      window.removeEventListener("drop", handlePageDrop);
    };
  }, []);

  return (
    <div className="container">
      <NavBar />


      <main className="image-analysis-main">
        <header
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            className="drawer-title"
            sx={{
              fontWeight: 600,
              fontFamily: "Host Grotesk, sans-serif",
            }}
          >
            Image Analysis
          </Typography>
        </header>

        <div className="content-wrapper" ref={ImageContainerRef}>
          <div
            className={`search-container ${isDragging ? "expanded" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="search-input-wrapper">
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Drop, paste, or upload an image"
                className="search-input"
                disabled={loading}
              />
              <div className="search-actions">
                <button
                  type="button"
                  className="icon-button"
                  disabled={loading}
                  onClick={handleBrowseClick}
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                </button>
              </div>
            </div>

            {isDragging && (
              <div
                className="drag-drop-area dragging"
                onClick={handleBrowseClick}
              >
                <FontAwesomeIcon icon={faCloudArrowUp} className="upload-icon" />
                <p className="drag-drop-title">Drop your image here</p>
                <p className="drag-drop-subtitle">or click to browse</p>
              </div>
            )}
          </div>

          {clipboardImage && !loading && (
            <div className="clipboard-suggestion">
              <div className="clipboard-content">
                <FontAwesomeIcon icon={faClipboard} className="clipboard-icon" />
                <span className="clipboard-text">
                  Image detected in clipboard
                </span>
                <button
                  className="clipboard-upload-btn"
                  onClick={handleClipboardUpload}
                >
                  <FontAwesomeIcon icon={faUpload} /> Upload from clipboard
                </button>
                <button
                  className="clipboard-dismiss-btn"
                  onClick={() => setClipboardImage(null)}
                  title="Dismiss"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
          )}

          {loading && (
            <div className="loading-container">
              <div className="loading-indicator">
                <FontAwesomeIcon icon={faSpinner} spin className="spinner" />
                Analyzing image...
              </div>
            </div>
          )}

          {images.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faImage} className="empty-icon" />
              <p className="empty-text">Upload an image to get started</p>
            </div>
          ) : (
            <div className="results-grid">
              {images.map((image, index) => (
                <Card
                  key={image._id}
                  item={image}
                  onOpen={openModal}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>

        <Modal
          open={modalOpen}
          onClose={closeModal}
          itemId={selectedId}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      </main>

      <ReactProSidebar />

      {/* Restricted file input — whitelist extensions */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.gif,.webp"
        style={{ display: "none" }}
        onChange={(e) =>
          e.target.files[0] && handleFileUpload(e.target.files[0])
        }
      />
    </div>
  );
}
